import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { nationsService, participantsService } from "../../services";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AdminModalTailwind from "./AdminModalTailwind";

function NationsTailwind() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [nations, setNations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Team-Management State
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [selectedNation, setSelectedNation] = useState(null);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });

  // Lade Daten beim ersten Laden
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Lade Nationen
      // Nutze aggregierte Backend-Statistik statt N+1-Requests
      const statsResponse = await nationsService.getParticipantStats();
      const nationsResponse = await nationsService.getAllNations();

      if (statsResponse.success && nationsResponse.success) {
        const statsMap = new Map(
          (statsResponse.data?.nations || []).map(n => [n.code, n.participantCount])
        );
        const merged = nationsResponse.data.map(nation => ({
          ...nation,
          participants: statsMap.get(nation.code) || 0,
          // Stelle sicher, dass participatesInTeamCompetition vorhanden ist
          participatesInTeamCompetition: nation.participatesInTeamCompetition || nation.participates_in_team_competition || false
        }));
        setNations(merged);
      } else {
        setError(nationsResponse.error);
      }
    } catch (err) {
      setError("Fehler beim Laden der Daten");
      console.error("Fehler beim Laden der Daten:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
  };

  const filterOptions = [
    { value: "all", label: t("admin.nations.filters.all", "Alle") },
    { value: "confirmed", label: t("admin.nations.filters.confirmed", "Bestätigt") },
    { value: "pending", label: t("admin.nations.filters.pending", "Ausstehend") }
  ];

  // Filter- und Suchlogik
  const filteredNations = nations.filter((nation) => {
    const matchesSearch = nation.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      selectedFilter === "all" ||
      (selectedFilter === "confirmed" && nation.status?.toLowerCase() === "bestätigt") ||
      (selectedFilter === "pending" && nation.status?.toLowerCase() === "ausstehend");
    return matchesSearch && matchesStatus;
  });

  // Navigation zur Teilnehmerverwaltung mit Nationenvorwahl
  const handleShowParticipants = (nationCode) => {
    navigate(`/admin/participants?nation=${nationCode}`);
  };

  // Toast-Funktionen
  const addToast = (type, message) => {
    setToast({ show: true, message, type });
    
    // Toast nach 5 Sekunden automatisch entfernen
    setTimeout(() => {
      setToast({ show: false, message: "", type: "info" });
    }, 5000);
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "info" });
  };

  // Available Participants Query (für Team-Management)
  const availableParticipantsQuery = useQuery({
    queryKey: ['availableParticipants', selectedNation?.id],
    queryFn: async () => {
      if (!selectedNation?.id) return [];
      const res = await nationsService.getAvailableParticipants(selectedNation.id);
      if (!res.success) throw new Error(res.error || 'Fehler beim Laden der Teilnehmer');
      return res.data || [];
    },
    enabled: !!selectedNation?.id && teamModalOpen
  });

  // Team Query (für angezeigte Team-Größe)
  const teamQuery = useQuery({
    queryKey: ['team', selectedNation?.id],
    queryFn: async () => {
      if (!selectedNation?.id) return null;
      const res = await nationsService.getNationTeam(selectedNation.id);
      if (!res.success) throw new Error(res.error || 'Fehler beim Laden des Teams');
      return res.data;
    },
    enabled: !!selectedNation?.id && teamModalOpen
  });

  // Mutation für Team-Teilnahme
  const updateTeamParticipationMutation = useMutation({
    mutationFn: async ({ nationId, participatesInTeamCompetition }) => {
      const res = await nationsService.updateTeamParticipation(nationId, participatesInTeamCompetition);
      if (!res.success) throw new Error(res.error || 'Fehler beim Aktualisieren der Team-Teilnahme');
      return res.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['nations']);
      loadData(); // Lade Nationen neu
      const nationName = nations.find(n => n.id === variables.nationId)?.name || 'Nation';
      if (variables.participatesInTeamCompetition) {
        addToast('success', `Team-Teilnahme für "${nationName}" erfolgreich aktiviert`);
      } else {
        addToast('success', `Team-Teilnahme für "${nationName}" erfolgreich deaktiviert`);
      }
    },
    onError: (error, variables) => {
      const nationName = nations.find(n => n.id === variables.nationId)?.name || 'Nation';
      addToast('error', `Fehler beim Aktualisieren der Team-Teilnahme für "${nationName}": ${error.message}`);
    }
  });

  // Mutation für Team-Mitglieder setzen
  const setTeamMembersMutation = useMutation({
    mutationFn: async ({ nationId, participantIds }) => {
      const res = await nationsService.setTeamMembers(nationId, participantIds);
      if (!res.success) throw new Error(res.error || 'Fehler beim Speichern der Team-Mitglieder');
      return res.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['team', selectedNation?.id]);
      queryClient.invalidateQueries(['availableParticipants', selectedNation?.id]);
      const nationName = selectedNation?.name || 'Nation';
      const teamSize = variables.participantIds.length;
      const wasExistingTeam = selectedTeamMembers.length > 0 && teamQuery.data?.teamMembers?.length > 0;
      setTeamModalOpen(false);
      addToast('success', `Team für "${nationName}" erfolgreich ${wasExistingTeam ? 'aktualisiert' : 'erstellt'} (${teamSize} Mitglieder)`);
    },
    onError: (error) => {
      const nationName = selectedNation?.name || 'Nation';
      addToast('error', `Fehler beim Speichern des Teams für "${nationName}": ${error.message}`);
    }
  });

  // Team-Management Handler
  const handleOpenTeamModal = (nation) => {
    setSelectedNation(nation);
    setTeamModalOpen(true);
    setSelectedTeamMembers([]); // Wird via useEffect aktualisiert, sobald teamQuery.data geladen ist
  };

  const handleCloseTeamModal = () => {
    setTeamModalOpen(false);
    setSelectedNation(null);
    setSelectedTeamMembers([]);
  };

  const handleToggleTeamParticipation = async (nation, checked) => {
    try {
      await updateTeamParticipationMutation.mutateAsync({
        nationId: nation.id,
        participatesInTeamCompetition: checked
      });
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Team-Teilnahme:', error);
      // Toast wird bereits in onError der Mutation angezeigt
    }
  };

  const handleToggleTeamMember = (participantId) => {
    setSelectedTeamMembers(prev => {
      if (prev.includes(participantId)) {
        return prev.filter(id => id !== participantId);
      } else {
        if (prev.length >= 12) {
          alert('Ein Team kann maximal 12 Mitglieder haben');
          return prev;
        }
        return [...prev, participantId];
      }
    });
  };

  const handleSaveTeamMembers = async () => {
    if (selectedTeamMembers.length < 6) {
      addToast('warning', 'Ein Team muss mindestens 6 Mitglieder haben');
      return;
    }
    if (selectedTeamMembers.length > 12) {
      addToast('warning', 'Ein Team kann maximal 12 Mitglieder haben');
      return;
    }
    try {
      await setTeamMembersMutation.mutateAsync({
        nationId: selectedNation.id,
        participantIds: selectedTeamMembers
      });
    } catch (error) {
      console.error('Fehler beim Speichern der Team-Mitglieder:', error);
      // Toast wird bereits in onError der Mutation angezeigt
    }
  };

  // Aktualisiere selectedTeamMembers wenn Team-Daten geladen werden
  useEffect(() => {
    if (teamQuery.data?.teamMembers && teamModalOpen) {
      setSelectedTeamMembers(teamQuery.data.teamMembers.map(tm => tm.participant.id));
    }
  }, [teamQuery.data, teamModalOpen]);

  if (loading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-64 mb-2"></div>
            <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-96"></div>
          </div>

          {/* Controls Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
              <div className="flex gap-2">
                <div className="w-24 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-md"></div>
              </div>
            </div>
          </div>

          {/* Filters Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20 mb-3"></div>
            <div className="flex flex-wrap gap-1.5">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-7 bg-slate-200 dark:bg-slate-700 rounded-full w-20"></div>
              ))}
            </div>
          </div>

          {/* Table Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-pulse">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    {[...Array(6)].map((_, i) => (
                      <th key={i} className="px-6 py-3">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {[...Array(8)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(6)].map((_, j) => (
                        <td key={j} className="px-6 py-4">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                  <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
                </div>
                <p className="text-slate-600 dark:text-slate-400 font-medium mb-4">{error}</p>
                <button 
                  onClick={loadData} 
                  className="bg-primary hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary text-white px-6 py-3 rounded-md transition-colors duration-200 font-medium"
                >
                  {t("admin.retry", "Erneut versuchen")}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                {t("admin.nations.title", "Nationenverwaltung")}
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-2">
                {t("admin.nations.subtitle", "Verwalte alle Nationen und deren Teilnehmer.")}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{filteredNations.length}</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">Nationen</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Controls Section */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder={t("admin.nations.search", "Nation suchen...")}
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 h-[46px] border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
              />
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 text-sm"></i>
            </div>

            {/* Add Button */}
            <button 
              className="bg-primary dark:bg-primary-light hover:bg-primary-dark dark:hover:bg-primary text-white px-4 h-[46px] rounded-md text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <i className="fas fa-plus text-xs"></i>
              {t("admin.nations.add", "Nation hinzufügen")}
            </button>
          </div>
        </div>

        {/* Filter Groups */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                {t("admin.nations.filters.title", "Status")}
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {filterOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleFilterChange(option.value)}
                    className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                      selectedFilter === option.value
                        ? 'bg-primary dark:bg-primary-light text-white'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-100 rounded-lg">
                <i className="fas fa-flag text-slate-600 text-xl"></i>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{nations.length}</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">Gesamte Nationen</div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-100 rounded-lg">
                <i className="fas fa-users text-slate-600 text-xl"></i>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {nations.reduce((sum, nation) => sum + nation.participants, 0)}
                </div>
                <div className="text-sm text-slate-500 dark:text-slate-400">Gesamte Teilnehmer</div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-100 rounded-lg">
                <i className="fas fa-chart-line text-slate-600 text-xl"></i>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {nations.length > 0 ? Math.round(nations.reduce((sum, nation) => sum + nation.participants, 0) / nations.length) : 0}
                </div>
                <div className="text-sm text-slate-500 dark:text-slate-400">Ø Teilnehmer pro Nation</div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
              <thead className="bg-slate-50 dark:bg-slate-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.nations.table.id", "ID")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.nations.table.name", "Name")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.nations.table.code", "Code")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.nations.table.participants", "Teilnehmer")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.nations.table.status", "Status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.nations.table.teamCompetition", "Gruppenwettbewerb")}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {t("admin.nations.table.actions", "Aktionen")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                {filteredNations.map((nation) => (
                  <tr key={nation.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-500 dark:text-slate-400">
                      {nation.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <img
                          src={process.env.PUBLIC_URL + "/images/flags/" + nation.code + ".png"}
                          alt={nation.name}
                          className="w-6 h-6 object-cover rounded-full border border-slate-200"
                        />
                        <div>
                          <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{nation.name}</div>
                          {nation.description && (
                            <div className="text-sm text-slate-500 dark:text-slate-400">{nation.description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-mono font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded">
                        {nation.code}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{nation.participants}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">Teilnehmer</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        nation.status?.toLowerCase() === 'bestätigt' 
                          ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                          : 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                      }`}>
                        {nation.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3 flex-wrap">
                        <label 
                          className="flex items-center gap-2 cursor-pointer group"
                        >
                          <input
                            type="checkbox"
                            checked={nation.participatesInTeamCompetition || false}
                            onChange={(e) => handleToggleTeamParticipation(nation, e.target.checked)}
                            disabled={updateTeamParticipationMutation.isLoading}
                            className="w-4 h-4 text-primary dark:text-primary-light border-slate-300 dark:border-slate-600 rounded focus:ring-primary focus:ring-2 cursor-pointer"
                          />
                          <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">
                            Teilnahme
                          </span>
                        </label>
                        {nation.participatesInTeamCompetition && (
                          <button
                            onClick={() => handleOpenTeamModal(nation)}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-md transition-colors"
                            title={t("admin.nations.actions.manageTeam", "Team verwalten")}
                          >
                            <i className="fas fa-users"></i>
                            <span>Team</span>
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center justify-end gap-1">
                        <button 
                          className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                          title={t("admin.nations.actions.edit", "Bearbeiten")}
                        >
                          <i className="fas fa-edit text-sm"></i>
                        </button>
                        <button 
                          onClick={() => handleShowParticipants(nation.code)}
                          className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                          title={t("admin.nations.actions.view", "Teilnehmer anzeigen")}
                        >
                          <i className="fas fa-eye text-sm"></i>
                        </button>
                        <button 
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title={t("admin.nations.actions.delete", "Löschen")}
                        >
                          <i className="fas fa-trash text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Empty State */}
        {filteredNations.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-flag text-slate-400 dark:text-slate-500 text-2xl"></i>
            </div>
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">Keine Nationen gefunden</h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6">
              {searchTerm || selectedFilter !== "all"
                ? "Versuchen Sie, Ihre Filter zu ändern oder den Suchbegriff anzupassen."
                : "Fügen Sie die erste Nation hinzu, um zu beginnen."
              }
            </p>
            {(!searchTerm && selectedFilter === "all") && (
              <button className="bg-primary dark:bg-primary-light hover:bg-primary-dark dark:hover:bg-primary text-white px-6 py-3 rounded-md transition-colors font-medium">
                <i className="fas fa-plus mr-2"></i>
                Erste Nation hinzufügen
              </button>
            )}
          </div>
        )}

        {/* Team-Management Modal */}
        <AdminModalTailwind
          isOpen={teamModalOpen}
          onClose={handleCloseTeamModal}
          title={`Team-Verwaltung: ${selectedNation?.name || ''}`}
          subtitle={`Wählen Sie 6-12 Teilnehmer für das Team aus`}
          onSave={handleSaveTeamMembers}
          onCancel={handleCloseTeamModal}
          saveButtonText="Team speichern"
          cancelButtonText="Abbrechen"
          isLoading={availableParticipantsQuery.isLoading}
          isSaving={setTeamMembersMutation.isLoading}
        >
          <div className="space-y-4">
            {availableParticipantsQuery.isLoading ? (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-100 rounded-full mb-4">
                  <i className="fas fa-spinner fa-spin text-slate-600"></i>
                </div>
                <p className="text-slate-600 font-medium">Teilnehmer werden geladen...</p>
              </div>
            ) : availableParticipantsQuery.error ? (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4">
                  <i className="fas fa-exclamation-triangle text-red-600"></i>
                </div>
                <p className="text-red-600 font-medium">
                  Fehler beim Laden der Teilnehmer: {availableParticipantsQuery.error.message}
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <span className="text-sm font-semibold text-slate-900">
                    Ausgewählt: {selectedTeamMembers.length} / 12
                  </span>
                  <span className={`text-sm font-medium ${
                    selectedTeamMembers.length >= 6 && selectedTeamMembers.length <= 12 
                      ? 'text-green-600' 
                      : 'text-red-600'
                  }`}>
                    {selectedTeamMembers.length < 6 
                      ? `Mindestens ${6 - selectedTeamMembers.length} weitere Teilnehmer erforderlich`
                      : selectedTeamMembers.length > 12
                      ? 'Maximal 12 Teilnehmer erlaubt'
                      : 'Team-Größe OK'}
                  </span>
                </div>

                <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-lg p-2">
                  {availableParticipantsQuery.data?.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="fas fa-user-slash text-slate-400 text-2xl"></i>
                      </div>
                      <p className="text-slate-500 font-medium">Keine Teilnehmer für diese Nation vorhanden</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {availableParticipantsQuery.data?.map((participant) => {
                        const isSelected = selectedTeamMembers.includes(participant.id);
                        return (
                          <label
                            key={participant.id}
                            className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              isSelected
                                ? 'bg-green-50 border-green-300 hover:bg-green-100'
                                : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleTeamMember(participant.id)}
                              className="w-5 h-5 text-primary dark:text-primary-light border-slate-300 dark:border-slate-600 rounded focus:ring-primary focus:ring-2 cursor-pointer"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-semibold text-slate-900">
                                {participant.name}
                              </div>
                              <div className="text-xs text-slate-500 mt-0.5">
                                #{participant.participantNumber} • {participant.age} Jahre • {participant.gender}
                              </div>
                            </div>
                            {participant.isInTeam && (
                              <span className="text-xs font-medium bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                Im Team
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </AdminModalTailwind>

        {/* Toast-Benachrichtigung */}
        {toast.show && (
          <div className="fixed top-4 right-4 z-50">
            <div className={`px-4 py-3 rounded-lg border shadow-lg bg-white ${
              toast.type === 'success' ? 'border-green-200' :
              toast.type === 'error' ? 'border-red-200' :
              toast.type === 'warning' ? 'border-yellow-200' :
              'border-blue-200'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                  toast.type === 'success' ? 'bg-green-100' :
                  toast.type === 'error' ? 'bg-red-100' :
                  toast.type === 'warning' ? 'bg-yellow-100' :
                  'bg-blue-100'
                }`}>
                  <i className={`fas text-xs ${
                    toast.type === 'success' ? 'fa-check text-green-600' :
                    toast.type === 'error' ? 'fa-times text-red-600' :
                    toast.type === 'warning' ? 'fa-exclamation-triangle text-yellow-600' :
                    'fa-info text-blue-600'
                  }`}></i>
                </div>
                <span className={`text-sm font-medium ${
                  toast.type === 'success' ? 'text-green-900' :
                  toast.type === 'error' ? 'text-red-900' :
                  toast.type === 'warning' ? 'text-yellow-900' :
                  'text-blue-900'
                }`}>{toast.message}</span>
                <button
                  onClick={hideToast}
                  className="ml-2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <i className="fas fa-times text-xs"></i>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

export default NationsTailwind;
